import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap } from 'rxjs/operators';
import { LoginService } from '../services/login.service';
import { LocalStorageUtil } from '../services/util/localstorage-util';
import { TemaDataService } from './tema-data.service';
import { CLASSE_TEMA_ESCURO, ehTemaValido, Tema, TEMA_PADRAO } from './tema.model';

@Injectable({ providedIn: 'root' })
export class TemaService {

    private temaSubject = new BehaviorSubject<Tema>(TEMA_PADRAO);
    public tema$: Observable<Tema> = this.temaSubject.asObservable();
    private inicializado = false;
    private versaoSalvar = 0;

    constructor(
        @Inject(DOCUMENT) private document: Document,
        private loginService: LoginService,
        private temaDataService: TemaDataService
    ) { }

    get temaAtual(): Tema {
        return this.temaSubject.value;
    }

    public inicializar(): void {
        if (this.inicializado) {
            return;
        }
        this.inicializado = true;

        const temaLocal = LocalStorageUtil.obterTema();
        this.aplicar(ehTemaValido(temaLocal) ? temaLocal : TEMA_PADRAO);

        this.loginService.token$.pipe(
            // Token vencido (ex.: login da identidade aberto depois da sessão expirar): não busca, evitando o 401 e o
            // logout que ele dispara.
            filter(token => !!token && !this.loginService.tokenExpirado()),
            switchMap(() => {
                // Descarta a resposta se o usuário salvou um tema enquanto a busca estava em andamento.
                const versao = this.versaoSalvar;
                return this.temaDataService.obter().pipe(
                    map(({ data }) => data?.tema),
                    catchError(() => of(undefined)),
                    filter(() => versao === this.versaoSalvar)
                );
            }),
            filter(ehTemaValido)
        ).subscribe(tema => this.aplicar(tema));
    }

    public aplicar(tema: Tema): void {
        this.document.body.classList.toggle(CLASSE_TEMA_ESCURO, tema === 'escuro');
        LocalStorageUtil.salvarTema(tema);
        this.temaSubject.next(tema);
    }

    public salvar(tema: Tema): Observable<boolean> {
        const anterior = this.temaAtual;
        this.versaoSalvar++;
        this.aplicar(tema);

        return this.temaDataService.salvar(tema).pipe(
            map(({ sucesso }) => {
                if (!sucesso) {
                    this.aplicar(anterior);
                }
                return sucesso;
            }),
            catchError(() => {
                this.aplicar(anterior);
                return of(false);
            })
        );
    }
}
